import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Globe, ArrowRight } from "lucide-react";

function CompanyLogo({ domain, name, size = 52 }) {
  const [useBackup, setUseBackup] = useState(false);
  const logoUrl = useBackup 
    ? `https://www.google.com/s2/favicons?sz=128&domain=${domain}`
    : `https://logo.clearbit.com/${domain}`;

  return (
    <img
      src={logoUrl}
      alt={`${name} logo`}
      onError={() => {
        if (!useBackup) setUseBackup(true);
      }}
      style={{ width: size, height: size }}
      className="rounded-2xl object-contain bg-white border border-gray-150 p-1.5 shrink-0 shadow-sm"
    />
  );
}

export default function StartupCard({ c, onClick }) {
  const navigate = useNavigate();
  const handleCardClick = () => {
    if (onClick) {
      onClick();
    } else {
      navigate(`/discover/company/${c.slug}`);
    }
  };

  return (
    <motion.div
      whileHover={{ y: -6 }}
      transition={{ duration: 0.2 }}
      onClick={handleCardClick}
      className="bg-white rounded-[20px] border border-gray-150 p-6 flex flex-col justify-between shadow-sm hover:shadow-md transition duration-300 relative group cursor-pointer"
    >
      <div>
        <div className="flex items-start justify-between gap-4">
          {c.domain ? (
            <CompanyLogo domain={c.domain} name={c.name} />
          ) : (
            <div className="w-[52px] h-[52px] rounded-2xl bg-gradient-to-br from-[#7C3AED] to-[#EC4899] text-white font-display font-semibold flex items-center justify-center shrink-0">
              {c.name ? c.name[0].toUpperCase() : "?"}
            </div>
          )}
          
          <div className="flex flex-wrap gap-1.5 justify-end">
            {c.stage && (
              <span className="text-[10px] font-bold px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded-full border border-indigo-100/50">
                {c.stage}
              </span>
            )}
            {c.funding && c.funding !== "N/A" && (
              <span className="text-[10px] font-bold px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded-full border border-emerald-100/50">
                {c.funding}
              </span>
            )}
          </div>
        </div>

        <div className="mt-4">
          <h3 className="font-display font-bold text-gray-950 text-lg leading-snug group-hover:text-[#7C3AED] transition">
            {c.name}
          </h3>
          <p className="text-xs text-gray-500 mt-1.5 leading-relaxed line-clamp-2">
            {c.description || "Empowering people with innovative tech solutions."}
          </p>
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-gray-100 flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between text-[11px] text-gray-400">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-gray-600 bg-gray-50 px-2 py-0.5 rounded">{c.industry}</span>
            <span>•</span>
            <span className="flex items-center gap-0.5 text-gray-500"><MapPin className="w-3 h-3 text-gray-300" /> {c.location || "India"}</span>
          </div>
          {c.founded_year && (
            <span className="text-[10px] font-medium text-gray-400">Est. {c.founded_year}</span>
          )}
        </div>

        <div className="flex items-center justify-between mt-1.5">
          {c.website ? (
            <a
              href={c.website}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-[11px] font-semibold text-gray-500 hover:text-[#7C3AED] transition flex items-center gap-1"
            >
              <Globe className="w-3.5 h-3.5" /> Website
            </a>
          ) : (
            <div />
          )}

          <button
            onClick={(e) => {
              e.stopPropagation();
              handleCardClick();
            }}
            className="text-[11px] font-bold text-gray-400 group-hover:text-[#7C3AED] transition flex items-center gap-0.5"
          >
            Open Profile <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}
